import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wallet, Banknote, Store, CheckCircle2, Upload } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Loader } from "../components/Loader";
import { PageHeader } from "../components/PageHeader";
import { fileToCompressedDataUrl } from "../lib/image";

export default function CartCheckout() {
  const [sp] = useSearchParams();
  const sellerId = sp.get("seller_id");
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [cart, setCart] = useState(null);
  const [cities, setCities] = useState([]);
  const [storeMethods, setStoreMethods] = useState([]);
  const [choice, setChoice] = useState("wallet");
  const [pickedMethod, setPickedMethod] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [dName, setDName] = useState(user?.name || "");
  const [dPhone, setDPhone] = useState(user?.phone || "");
  const [dCity, setDCity] = useState("");
  const [dRegion, setDRegion] = useState("");
  const [dAddress, setDAddress] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const c = await api("/cart");
        setCart(c);
        try { setCities(await api("/cities")); } catch {}
        if (sellerId) { try { setStoreMethods(await api(`/sellers/${sellerId}/payment-methods`) || []); } catch {} }
      } catch (e) { toast.error(e.message); }
    })();
  }, [sellerId]);

  if (!cart) return <Loader />;
  const groups = sellerId ? cart.groups.filter((g) => g.seller_id === sellerId) : cart.groups;
  const total = groups.reduce((s, g) => s + g.subtotal, 0);
  const walletBal = Number(user?.wallet_balance || 0);
  const insufficient = choice === "wallet" && walletBal < total;

  const onReceipt = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { setReceipt(await fileToCompressedDataUrl(f, 1080, 0.5)); } catch { toast.error("فشل رفع الإيصال"); }
  };

  const submit = async () => {
    if (!dName.trim()) return toast.error("الاسم مطلوب");
    if (!dPhone.trim()) return toast.error("رقم الهاتف مطلوب");
    if (!dCity.trim()) return toast.error("المدينة مطلوبة");
    if (!dRegion.trim()) return toast.error("المنطقة مطلوبة");
    let pmId = "wallet";
    if (choice === "cod") pmId = "cod";
    else if (choice === "store") {
      if (!pickedMethod) return toast.error("اختر طريقة الدفع");
      if (!receipt) return toast.error("ارفع إيصال التحويل");
      pmId = `store:${pickedMethod.name}`;
    } else if (insufficient) return toast.error("الرصيد غير كافٍ");
    setBusy(true);
    try {
      await api("/cart/checkout", { method: "POST", body: JSON.stringify({
        payment_method_id: pmId, delivery_name: dName.trim(), delivery_phone: dPhone.trim(), delivery_city: dCity.trim(),
        delivery_region: dRegion.trim(), delivery_address: dAddress.trim() || undefined, buyer_note: note.trim() || undefined,
        receipt_base64: receipt, seller_id: sellerId || undefined,
      }) });
      toast.success("تم إنشاء الطلب");
      refresh();
      navigate("/orders");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="animate-fade-in-up max-w-2xl mx-auto">
      <PageHeader title="الدفع من السلة" subtitle={`${groups.length} متجر • ${total.toFixed(2)} د.ل`} />
      <div className="space-y-3">
        {groups.map((g) => (
          <div key={g.seller_id} className="bg-white rounded-2xl border border-[#E8EAF2] p-4">
            <div className="flex items-center gap-2 mb-2"><Store size={16} className="text-[#FF3D5A]" /><span className="font-black text-[#161629] flex-1">{g.seller_name}</span><span className="font-black text-[#FF3D5A]">{g.subtotal.toFixed(2)} د.ل</span></div>
            {g.items.map((it) => <div key={it.item_id} className="flex justify-between text-sm py-1 text-[#6B7280]"><span>{it.product_name} × {it.quantity}</span><span className="font-bold">{(it.unit_price * it.quantity).toFixed(2)}</span></div>)}
          </div>
        ))}
      </div>

      <h2 className="text-base font-black text-[#161629] mt-6 mb-1">📦 معلومات التوصيل</h2>
      <F label="الاسم *" v={dName} on={setDName} t="cc-name" />
      <F label="رقم الهاتف *" v={dPhone} on={setDPhone} t="cc-phone" />
      <p className="text-sm font-bold text-[#161629] mt-4 mb-1.5">المدينة *</p>
      <div className="flex gap-2 flex-wrap">
        {cities.map((c) => <button key={c.city_id} onClick={() => setDCity(c.name)} className={`px-4 py-2 rounded-xl border font-bold text-sm ${dCity === c.name ? "bg-[#FF3D5A] border-[#FF3D5A] text-white" : "bg-white border-[#E8EAF2]"}`}>{c.name}</button>)}
      </div>
      <F label="المنطقة *" v={dRegion} on={setDRegion} t="cc-region" />
      <F label="تفاصيل العنوان (اختياري)" v={dAddress} on={setDAddress} t="cc-address" />

      <h2 className="text-base font-black text-[#161629] mt-6 mb-2">💳 طريقة الدفع</h2>
      <Opt t="cc-wallet" a={choice === "wallet"} on={() => setChoice("wallet")} I={Wallet} c="#00D26A" title="المحفظة" d={insufficient ? `الرصيد غير كافٍ (${walletBal.toFixed(2)})` : `الرصيد: ${walletBal.toFixed(2)} د.ل`} danger={insufficient} />
      <Opt t="cc-cod" a={choice === "cod"} on={() => setChoice("cod")} I={Banknote} c="#16a34a" title="نقداً عند الاستلام" d="ادفع عند الاستلام" />
      {sellerId && storeMethods.length > 0 && <Opt t="cc-store" a={choice === "store"} on={() => setChoice("store")} I={Store} c="#FF8A3C" title="طرق دفع المتجر" d={`${storeMethods.length} طريقة`} />}
      {choice === "store" && (
        <div className="p-3 bg-[#F7F8FE] rounded-2xl mt-2 space-y-2">
          {storeMethods.map((m) => <button key={m.name} onClick={() => setPickedMethod(m)} className={`w-full flex items-center gap-2 p-3 rounded-xl border-2 text-start ${pickedMethod?.name === m.name ? "border-[#FF3D5A] bg-[#FFE5EA]" : "border-[#E8EAF2] bg-white"}`}><div className="flex-1"><p className="font-black text-sm">{m.name}</p>{m.details && <p className="text-xs text-[#6B7280]">{m.details}</p>}</div>{pickedMethod?.name === m.name && <CheckCircle2 size={20} className="text-[#FF3D5A]" />}</button>)}
          {pickedMethod && <label className="flex flex-col items-center justify-center h-36 rounded-2xl border-2 border-dashed border-[#FF3D5A] bg-[#FFE5EA] cursor-pointer overflow-hidden">{receipt ? <img src={receipt} alt="" className="w-full h-full object-cover" /> : <><Upload size={28} className="text-[#FF3D5A]" /><span className="text-sm text-[#FF3D5A] font-bold mt-2">ارفع صورة الإيصال</span></>}<input type="file" accept="image/*" className="hidden" onChange={onReceipt} /></label>}
        </div>
      )}

      <button data-testid="cart-checkout-submit" disabled={busy} onClick={submit} className="w-full h-14 rounded-2xl bg-[#FF3D5A] text-white font-black mt-5 disabled:opacity-60">{busy ? "..." : `تأكيد الطلب (${total.toFixed(2)} د.ل)`}</button>
    </div>
  );
}
function F({ label, v, on, t }) { return <><p className="text-sm font-bold text-[#161629] mt-4 mb-1.5">{label}</p><input data-testid={t} value={v} onChange={(e) => on(e.target.value)} className="w-full bg-white rounded-xl px-4 py-3 border border-[#E8EAF2] outline-none focus:ring-2 focus:ring-[#FF3D5A]" /></>; }
function Opt({ a, on, I, c, title, d, t, danger }) { return <button data-testid={t} onClick={on} className={`w-full flex items-center gap-3 p-4 rounded-2xl border-2 mt-2 text-start ${a ? "border-[#FF3D5A] bg-[#FFE5EA]" : "border-[#E8EAF2] bg-white"}`}><div className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: c }}><I size={22} /></div><div className="flex-1"><p className="font-black text-[#161629]">{title}</p><p className={`text-xs ${danger ? "text-[#FF3D5A] font-black" : "text-[#6B7280]"}`}>{d}</p></div>{a && <CheckCircle2 size={22} className="text-[#FF3D5A]" />}</button>; }
